import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { cn } from '@client/utils/styling/cn';
import type { ReactElement, ReactNode } from 'react';

interface LinkTooltipProps {
    /** The styled anchor */
    children: ReactElement;
    /** Whether the link is disabled */
    disabled?: boolean;
    /** What unblocks the link; shown on hover or focus while disabled */
    disabledReason?: ReactNode;
    /** Tooltip shown while the link is enabled */
    tooltip?: ReactNode;
    /** The link fills its container, so the disabled wrapper must too */
    block?: boolean;
}

const hasContent = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== '';

/**
 * Tooltip for the ButtonModern link variants.
 *
 * A disabled link sets `pointer-events-none`, so a tooltip on the anchor itself never opens. While
 * disabled with a `disabledReason`, the tooltip anchors to a focusable wrapping span instead, so
 * hovering or tabbing to the control explains what unblocks it. The base `Button`, and through it
 * `Button.Selection`'s state reason, use the same wrapper. A block link keeps its full width: the
 * wrapper fills the container rather than shrinking to the anchor.
 */
export function LinkTooltip({ children, disabled, disabledReason, tooltip, block }: LinkTooltipProps) {
    if (disabled && hasContent(disabledReason)) {
        return (
            <Tooltip title={disabledReason} trigger={['hover', 'focus']}>
                <span className={cn('cursor-not-allowed', block ? 'flex w-full' : 'inline-flex')} tabIndex={0}>
                    {children}
                </span>
            </Tooltip>
        );
    }

    if (!disabled && hasContent(tooltip)) {
        return <Tooltip title={tooltip}>{children}</Tooltip>;
    }

    return children;
}
